import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-redux-i18n';

const PAD = { top: 28, right: 16, bottom: 40, left: 64 };
const CALL_COLOR = '#22c55e';
const PUT_COLOR = '#ef4444';
const NET_POS = '#22c55e';
const NET_NEG = '#ef4444';
const DEFAULT_VISIBLE = 36;
const MIN_VISIBLE = 8;

function formatAxis(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) {
    return '0';
  }
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) {
    return `${sign}${(abs / 1e9).toFixed(1)}B`;
  }
  if (abs >= 1e6) {
    return `${sign}${(abs / 1e6).toFixed(1)}M`;
  }
  if (abs >= 1e3) {
    return `${sign}${(abs / 1e3).toFixed(0)}K`;
  }
  return `${sign}${abs.toFixed(0)}`;
}

function formatStrike(strike) {
  const n = Number(strike);
  if (!Number.isFinite(n)) {
    return '';
  }
  return n.toFixed(n % 1 === 0 ? 0 : 1);
}

function nearestIndex(rows, value) {
  if (!rows.length || !Number.isFinite(value)) {
    return Math.floor(rows.length / 2);
  }
  let best = 0;
  let bestDist = Math.abs(rows[0].strike - value);
  for (let i = 1; i < rows.length; i += 1) {
    const d = Math.abs(rows[i].strike - value);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

function buildWindow(total, centerIdx, visible) {
  if (total <= 0) {
    return { start: 0, end: 0 };
  }
  const count = Math.min(Math.max(visible, MIN_VISIBLE), total);
  let start = Math.round(centerIdx - count / 2);
  start = Math.max(0, Math.min(start, total - count));
  return { start, end: start + count };
}

/**
 * Call GEX above zero (green) / Put GEX below (red), or Net mode.
 * Wheel = zoom strikes · drag = pan · Reset restores spot-centered view.
 */
function GexChart({
  strikes = [],
  spot,
  gammaFlip,
  putWall,
  callWall,
  mode = 'split',
  width = 960,
  height = 420,
}) {
  const svgRef = useRef(null);
  const dragRef = useRef(null);

  const allRows = useMemo(
    () => (Array.isArray(strikes) ? strikes.filter((r) => Number.isFinite(r.strike)) : []),
    [strikes]
  );
  const total = allRows.length;

  const defaultWindow = useMemo(() => {
    const center = nearestIndex(allRows, spot);
    const visible = Math.min(DEFAULT_VISIBLE, Math.max(total, MIN_VISIBLE));
    return buildWindow(total, center, visible);
  }, [allRows, spot, total]);

  const [win, setWin] = useState(defaultWindow);

  useEffect(() => {
    setWin(defaultWindow);
  }, [defaultWindow]);

  const visibleCount = Math.max(win.end - win.start, 1);
  const isZoomed = total > 0 && visibleCount < total;

  const layout = useMemo(() => {
    const rows = allRows.slice(win.start, win.end);
    const n = rows.length;
    const innerW = Math.max(width - PAD.left - PAD.right, 120);
    const innerH = Math.max(height - PAD.top - PAD.bottom, 120);
    const midY = PAD.top + innerH / 2;

    let maxAbs = 1;
    rows.forEach((r) => {
      if (mode === 'net') {
        maxAbs = Math.max(maxAbs, Math.abs(Number(r.netGex) || 0));
      } else {
        maxAbs = Math.max(
          maxAbs,
          Math.abs(Number(r.callGex) || 0),
          Math.abs(Number(r.putGex) || 0)
        );
      }
    });

    const barGap = n > 50 ? 1 : n > 24 ? 2 : 3;
    const slot = n > 0 ? innerW / n : innerW;
    const barW = Math.max(2, slot - barGap);
    const yScale = (v) => (Math.abs(v) / maxAbs) * (innerH / 2 - 4);
    const xCenter = (i) => PAD.left + i * slot + slot / 2;

    const strikeToX = (strike) => {
      if (!n || !Number.isFinite(strike)) {
        return null;
      }
      const globalIdx = nearestIndex(allRows, strike);
      if (globalIdx < win.start || globalIdx >= win.end) {
        return null;
      }
      return xCenter(globalIdx - win.start);
    };

    return { rows, n, innerW, innerH, midY, maxAbs, barW, slot, yScale, xCenter, strikeToX };
  }, [allRows, win, mode, width, height]);

  const { rows, n, midY, maxAbs, barW, slot, yScale, xCenter, strikeToX, innerW } = layout;

  const clampWindow = useCallback((start, end) => {
    if (total <= 0) {
      return { start: 0, end: 0 };
    }
    let count = Math.round(end - start);
    count = Math.min(Math.max(count, MIN_VISIBLE), total);
    let s = Math.round(start);
    s = Math.max(0, Math.min(s, total - count));
    return { start: s, end: s + count };
  }, [total]);

  const clientToLocalX = useCallback((clientX) => {
    const el = svgRef.current;
    if (!el) {
      return null;
    }
    const rect = el.getBoundingClientRect();
    if (!rect.width) {
      return null;
    }
    return ((clientX - rect.left) / rect.width) * width;
  }, [width]);

  const zoomAt = useCallback((localX, factor) => {
    if (total <= MIN_VISIBLE) {
      return;
    }
    setWin((prev) => {
      const count = prev.end - prev.start;
      const nextCount = Math.round(count * factor);
      const clampedCount = Math.min(Math.max(nextCount, MIN_VISIBLE), total);
      if (clampedCount === count) {
        return prev;
      }
      const plotX = Math.min(Math.max(localX - PAD.left, 0), Math.max(innerW, 1));
      const frac = plotX / Math.max(innerW, 1);
      const anchor = prev.start + frac * count;
      const start = anchor - frac * clampedCount;
      return clampWindow(start, start + clampedCount);
    });
  }, [total, innerW, clampWindow]);

  const onWheel = useCallback((event) => {
    event.preventDefault();
    const localX = clientToLocalX(event.clientX);
    if (localX == null) {
      return;
    }
    const factor = event.deltaY > 0 ? 1.18 : 0.85;
    zoomAt(localX, factor);
  }, [clientToLocalX, zoomAt]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) {
      return undefined;
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  const onPointerDown = useCallback((event) => {
    if (event.button !== 0 || total <= visibleCount) {
      return;
    }
    const localX = clientToLocalX(event.clientX);
    if (localX == null) {
      return;
    }
    dragRef.current = {
      pointerId: event.pointerId,
      startX: localX,
      startWin: { ...win },
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [clientToLocalX, total, visibleCount, win]);

  const onPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const localX = clientToLocalX(event.clientX);
    if (localX == null || !slot) {
      return;
    }
    const dx = localX - drag.startX;
    const shift = Math.round(-dx / slot);
    const count = drag.startWin.end - drag.startWin.start;
    setWin(clampWindow(drag.startWin.start + shift, drag.startWin.start + shift + count));
  }, [clientToLocalX, slot, clampWindow]);

  const onPointerUp = useCallback((event) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  }, []);

  const resetZoom = useCallback(() => {
    setWin(defaultWindow);
  }, [defaultWindow]);

  if (!total) {
    return (
      <div className="gex-chart gex-chart--empty">
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" />
      </div>
    );
  }

  const markers = [
    { key: 'spot', value: spot, color: '#38bdf8', label: 'Spot' },
    { key: 'flip', value: gammaFlip, color: '#fbbf24', label: 'Flip' },
    { key: 'putWall', value: putWall, color: '#f87171', label: 'Put Wall' },
    { key: 'callWall', value: callWall, color: '#4ade80', label: 'Call Wall' },
  ].filter((m) => Number.isFinite(m.value));

  const tickCount = 4;
  const yTicks = [];
  for (let i = tickCount; i >= -tickCount; i -= 1) {
    if (i === 0) {
      continue;
    }
    yTicks.push((i / tickCount) * maxAbs);
  }

  const labelEvery = Math.max(1, Math.ceil(n / Math.min(14, Math.max(n, 1))));

  return (
    <div className="gex-chart">
      <div className="gex-chart-controls">
        <span className="gex-chart-zoom-hint">
          <Translate value="gex_zoom_hint" />
        </span>
        <div className="gex-chart-zoom-btns">
          <button
            type="button"
            className="gex-zoom-btn"
            onClick={() => zoomAt(PAD.left + innerW / 2, 0.75)}
            aria-label="Zoom in"
          >
            +
          </button>
          <button
            type="button"
            className="gex-zoom-btn"
            onClick={() => zoomAt(PAD.left + innerW / 2, 1.35)}
            aria-label="Zoom out"
          >
            −
          </button>
          <button
            type="button"
            className="gex-zoom-btn gex-zoom-btn--reset"
            onClick={resetZoom}
            disabled={win.start === defaultWindow.start && win.end === defaultWindow.end}
          >
            <Translate value="gex_zoom_reset" />
          </button>
        </div>
      </div>

      <svg
        ref={svgRef}
        className={`gex-chart-svg${isZoomed ? ' is-zoomed' : ''}`}
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Gamma exposure by strike"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <rect
          x={PAD.left}
          y={PAD.top}
          width={innerW}
          height={height - PAD.top - PAD.bottom}
          fill="transparent"
        />

        <line
          x1={PAD.left}
          y1={midY}
          x2={width - PAD.right}
          y2={midY}
          className="gex-chart-zero"
        />

        {yTicks.map((val) => {
          const y = midY - Math.sign(val) * yScale(val);
          return (
            <g key={`yt-${val}`}>
              <line
                x1={PAD.left}
                y1={y}
                x2={width - PAD.right}
                y2={y}
                className="gex-chart-grid"
              />
              <text x={PAD.left - 8} y={y + 3} textAnchor="end" className="gex-chart-axis-label">
                {formatAxis(val)}
              </text>
            </g>
          );
        })}

        {rows.map((row, i) => {
          const cx = xCenter(i);
          const x = cx - barW / 2;
          if (mode === 'net') {
            const net = Number(row.netGex) || 0;
            const h = yScale(net);
            const y = net >= 0 ? midY - h : midY;
            return (
              <rect
                key={`n-${row.strike}`}
                x={x}
                y={y}
                width={barW}
                height={Math.max(h, 0.5)}
                fill={net >= 0 ? NET_POS : NET_NEG}
                opacity={0.9}
              >
                <title>{`Strike ${formatStrike(row.strike)}: Net ${formatAxis(net)}`}</title>
              </rect>
            );
          }
          const callGex = Number(row.callGex) || 0;
          const putGex = Number(row.putGex) || 0;
          const callH = yScale(callGex);
          const putH = yScale(putGex);
          return (
            <g key={`s-${row.strike}`}>
              <rect
                x={x}
                y={midY - callH}
                width={barW}
                height={Math.max(callH, 0)}
                fill={CALL_COLOR}
                opacity={0.9}
              >
                <title>{`Strike ${formatStrike(row.strike)}: Call ${formatAxis(callGex)}`}</title>
              </rect>
              <rect
                x={x}
                y={midY}
                width={barW}
                height={Math.max(putH, 0)}
                fill={PUT_COLOR}
                opacity={0.9}
              >
                <title>{`Strike ${formatStrike(row.strike)}: Put ${formatAxis(putGex)}`}</title>
              </rect>
            </g>
          );
        })}

        {markers.map((m) => {
          const x = strikeToX(m.value);
          if (x == null) {
            return null;
          }
          return (
            <g key={m.key}>
              <line
                x1={x}
                y1={PAD.top}
                x2={x}
                y2={height - PAD.bottom}
                stroke={m.color}
                strokeWidth={1.5}
                strokeDasharray={m.key === 'spot' ? '4 3' : '2 4'}
                opacity={0.95}
              />
              <text
                x={x + 4}
                y={PAD.top + 12}
                className="gex-chart-marker-label"
                fill={m.color}
              >
                {m.label} {formatStrike(m.value)}
              </text>
            </g>
          );
        })}

        {rows.map((row, i) => {
          if (i % labelEvery !== 0 && i !== n - 1) {
            return null;
          }
          return (
            <text
              key={`xl-${row.strike}`}
              x={xCenter(i)}
              y={height - 14}
              textAnchor="middle"
              className="gex-chart-axis-label gex-chart-strike-label"
            >
              {formatStrike(row.strike)}
            </text>
          );
        })}
      </svg>

      <div className="gex-chart-range">
        {formatStrike(allRows[win.start]?.strike)}
        {' — '}
        {formatStrike(allRows[Math.max(win.end - 1, win.start)]?.strike)}
        <span className="gex-chart-range-count">
          {' '}({visibleCount}/{total})
        </span>
      </div>
    </div>
  );
}

GexChart.propTypes = {
  strikes: PropTypes.arrayOf(PropTypes.object),
  spot: PropTypes.number,
  gammaFlip: PropTypes.number,
  putWall: PropTypes.number,
  callWall: PropTypes.number,
  mode: PropTypes.oneOf(['split', 'net']),
  width: PropTypes.number,
  height: PropTypes.number,
};

export default GexChart;
